import type { Point } from '../../doc/geometry';
import type { PaneStore } from '../paneStore';
import { centerOn, screenToWorld, type Camera } from './camera';

/**
 * Animated camera moves ("fast travel" to a frame or a saved view).
 *
 * The path is van Wijk and Nuij's smooth zoom and pan (the one d3's interpolateZoom uses): a
 * long trip zooms out, travels and zooms back in, a short one barely leaves the zoom, and the
 * apparent speed stays even. Any other camera change on the pane (a wheel, a drag, a pinch, a
 * key) ends the flight where it is; a new flight replaces the one under way.
 */

/** Curvature: how far a long trip zooms out on the way (d3's default, sqrt 2). */
const RHO = Math.SQRT2;
const RHO2 = RHO * RHO;
const RHO4 = RHO2 * RHO2;
/** Milliseconds per unit of path length, and the bounds of a flight. */
const MS_PER_S = 380;
const MIN_MS = 260;
const MAX_MS = 1100;

interface ZoomPath {
  /** Path length in van Wijk's units (sign-free). */
  length: number;
  /** World centre and visible world width at t in [0, 1]. */
  at(t: number): { c: Point; w: number };
}

/** From centre c0 showing a world width w0 to c1 showing w1. */
function zoomPath(c0: Point, w0: number, c1: Point, w1: number): ZoomPath {
  const dx = c1.x - c0.x;
  const dy = c1.y - c0.y;
  const d2 = dx * dx + dy * dy;
  if (d2 < 1e-12) {
    // only the zoom changes
    const S = Math.log(w1 / w0) / RHO;
    return { length: Math.abs(S), at: (t) => ({ c: { x: c0.x + t * dx, y: c0.y + t * dy }, w: w0 * Math.exp(RHO * t * S) }) };
  }
  const d1 = Math.sqrt(d2);
  const b0 = (w1 * w1 - w0 * w0 + RHO4 * d2) / (2 * w0 * RHO2 * d1);
  const b1 = (w1 * w1 - w0 * w0 - RHO4 * d2) / (2 * w1 * RHO2 * d1);
  const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0);
  const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1);
  const S = (r1 - r0) / RHO;
  const coshR0 = Math.cosh(r0);
  return {
    length: Math.abs(S),
    at(t) {
      const s = t * S;
      const u = (w0 / (RHO2 * d1)) * (coshR0 * Math.tanh(RHO * s + r0) - Math.sinh(r0));
      return { c: { x: c0.x + u * dx, y: c0.y + u * dy }, w: (w0 * coshR0) / Math.cosh(RHO * s + r0) };
    },
  };
}

const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The next animation frame, or 50 ms if frames are not coming (a page the browser is not
 * drawing pauses requestAnimationFrame; the flight should still land).
 */
function nextFrame(cb: () => void): void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cb();
  };
  requestAnimationFrame(run);
  setTimeout(run, 50);
}

/** Stops the flight under way on each pane. */
const flights = new WeakMap<PaneStore, () => void>();

/** Flies the pane's camera to `target`. Instant when the user prefers reduced motion. */
export function flyTo(pane: PaneStore, target: Camera): void {
  flights.get(pane)?.();
  const { camera: from, size } = pane.getState();
  if (reducedMotion() || size.w <= 0 || size.h <= 0) {
    pane.getState().setCamera(target);
    return;
  }
  const mid = { x: size.w / 2, y: size.h / 2 };
  const path = zoomPath(screenToWorld(from, mid), size.w / from.zoom, screenToWorld(target, mid), size.w / target.zoom);
  if (path.length < 1e-6 && from.rot === target.rot) {
    pane.getState().setCamera(target);
    return;
  }
  const duration = Math.min(MAX_MS, Math.max(MIN_MS, path.length * MS_PER_S));
  // turn the short way round
  const dRot = ((((target.rot - from.rot) % 360) + 540) % 360) - 180;
  const start = performance.now();
  let last = from;
  let stopped = false;
  const stop = () => {
    stopped = true;
    if (flights.get(pane) === stop) flights.delete(pane);
  };
  const step = () => {
    if (stopped) return;
    const st = pane.getState();
    // someone else moved the camera: leave it there
    if (st.camera !== last) return stop();
    const t = Math.min(1, (performance.now() - start) / duration);
    let cam: Camera;
    if (t >= 1) cam = target;
    else {
      const p = path.at(easeInOut(t));
      cam = centerOn(p.c, st.size, size.w / p.w, from.rot + dRot * easeInOut(t));
    }
    st.setCamera(cam);
    last = cam;
    if (t < 1) nextFrame(step);
    else stop();
  };
  flights.set(pane, stop);
  nextFrame(step);
}
